/**
 * SignatureState
 * Manages the state of all signers and their signatures
 */

import { i18n } from '../utils/i18n.js';

export class SignatureState {
  constructor(signers = [], integrationData = {}, settings = {}) {
    this.signers = this.initializeSigners(signers);
    this.signatures = new Map();
    this.listeners = [];
    this.integrationData = integrationData; // Pass-through data
    this.settings = settings;
  }

  /**
   * Initialize signers with default values
   */
  initializeSigners(signers) {
    return signers.map((signer, index) => ({
      _index: index,
      signerId: signer.signerId,
      label: signer.label || i18n.t('signers.defaultLabel'),
      isMandatory: signer.isMandatory !== false,
      isSigned: signer.isSigned || false,
      timestamp: signer.timestamp || null,
      signatureType: signer.signatureType || 'dtu',
      signerListId: signer.signerListId || null,
      signatureData: null,
      // Preserve WebSign configuration
      widget: signer.widget || null,
      ebpText: signer.ebpText || null,
      tspActivate: signer.tspActivate || false,
      tspUrl: signer.tspUrl || null,
      tspUser: signer.tspUser || null,
      tspPassword: signer.tspPassword || null,
      ocspActivate: signer.ocspActivate || false,
      ocspUrl: signer.ocspUrl || null,
      ocspUser: signer.ocspUser || null,
      ocspPassword: signer.ocspPassword || null,
      autoSign: signer.autoSign || false,
      // Images for display
      img1B64: signer.img1B64 || null,
      img2B64: signer.img2B64 || null
    }));
  }

  /**
   * Get all signers
   */
  getSigners() {
    return [...this.signers];
  }

  /**
   * Get a specific signer
   */
  getSigner(index) {
    return this.signers.find(s => s._index === index);
  }

  getSignerById(signerId) {
    return this.signers.find(s => s.signerId === signerId);
  }

  /**
   * Mark a signer as signed
   */
  markAsSigned(index, signatureData) {
    const signer = this.getSigner(index);
    if (!signer) {
      throw new Error(`Signer not found at index: ${index}`);
    }

    signer.isSigned = true;
    signer.timestamp = new Date().toISOString();
    signer.signatureData = signatureData;

    this.signatures.set(index, signatureData);

    // If requireAllSignerOccurrences is enabled, make all other unsigned occurrences mandatory
    if (this.settings.requireAllSignerOccurrences) {
      this.signers.forEach(s => {
        if (s.signerId === signer.signerId && s._index !== index && !s.isSigned) {
          s.isMandatory = true;
        }
      });
    }

    this.notifyChange();
  }

  /**
   * Update signers list
   */
  updateSigners(signers) {
    this.signers = this.initializeSigners(signers);
    this.notifyChange();
  }

  /**
   * Check if all mandatory signers have signed
   */
  canComplete() {
    const mandatorySigners = this.signers.filter(s => s.isMandatory);
    return mandatorySigners.every(s => s.isSigned);
  }

  /**
   * Check if at least one signer (mandatory or not) has signed
   */
  hasAnySignature() {
    return this.signers.some(s => s.isSigned);
  }

  /**
   * Check if at least one mandatory signer has signed
   */
  hasMandatorySignature() {
    return this.signers.some(s => s.isMandatory && s.isSigned);
  }

  /**
   * Check if all signers have signed
   */
  isComplete() {
    return this.signers.every(s => s.isSigned);
  }

  /**
   * Get all signatures
   */
  getAllSignatures() {
    return Array.from(this.signatures.entries()).map(([signerId, data]) => ({
      signerId,
      ...data
    }));
  }

  /**
   * Get integration data (pass-through)
   */
  getIntegrationData() {
    return this.integrationData;
  }

  /**
   * Get signature for a specific signer
   */
  getSignature(index) {
    return this.signatures.get(index);
  }

  /**
   * Get progress statistics
   */
  getProgress() {
    const total = this.signers.length;
    const signed = this.signers.filter(s => s.isSigned).length;
    const mandatory = this.signers.filter(s => s.isMandatory).length;
    const mandatorySigned = this.signers.filter(s => s.isMandatory && s.isSigned).length;

    return {
      total,
      signed,
      pending: total - signed,
      mandatory,
      mandatorySigned,
      mandatoryPending: mandatory - mandatorySigned,
      percentage: total > 0 ? Math.round((signed / total) * 100) : 0
    };
  }

  /**
   * Subscribe to state changes
   */
  on(event, callback) {
    if (event === 'change') {
      this.listeners.push(callback);
    }
  }

  /**
   * Unsubscribe from state changes
   */
  off(event, callback) {
    if (event === 'change') {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    this.listeners = [];
  }

  /**
   * Notify all listeners of state change
   */
  notifyChange() {
    this.listeners.forEach(callback => callback(this));
  }

  /**
   * Reset all signatures
   */
  reset() {
    this.signers.forEach(signer => {
      signer.isSigned = false;
      signer.timestamp = null;
      signer.signatureData = null;
    });
    this.signatures.clear();
    this.notifyChange();
  }
}
